import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { dodAtom } from '@components/metrics-panel-hz/atoms';
import instance from '@src/api/axiosInstance';
import getMetricsData from '@src/api/metricContainer';
import { trackQueryError } from '@utils/sensor/apptracker';
import { getFirstAppliedFilter, realTimeModeOnTrendsSelector } from '@store/common/selectors';
import { comparisonIntervalSelector } from '@store/summaryContainer/comparison/selectors';
import { useAtomValue } from 'jotai/react';
import { get, isEmpty } from 'lodash';
import { createQuery } from './useFetchSummaryPanelData';

const defaultResponseValue = { totals: {}, data: {} };

// hook to fetch dod data for widget panel metrics
export function useFetchDod({ enabled = true, metricsList = [] }: any) {
  const interval: any = useSelector(comparisonIntervalSelector);
  const realTimeOnTrends: boolean = useSelector(realTimeModeOnTrendsSelector);
  const { rule: filter, _rKey = 0 } = useSelector(getFirstAppliedFilter);

  const payload = createQuery(
    metricsList,
    `${interval.from}/${interval.to}`,
    filter,
    realTimeOnTrends,
  );
  const dodEnabled = useAtomValue(dodAtom) && !isEmpty(interval) && !isEmpty(interval.from);

  const {
    isFetching,
    isError,
    data: response,
  } = useQuery(
    ['dod-summary-widgets', payload, _rKey],
    () => {
      return getMetricsData(payload, {}, instance).then((response) => {
        const { data, totals } = get(response, ['data', '0'], []);
        return { data, totals };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: 3,
      enabled: enabled && dodEnabled && metricsList.length > 0,
      staleTime: 5 * 60 * 1000, // 5m
      placeholderData: {
        data: {},
        totals: {},
      },
      onError: (err) => {
        trackQueryError({ type: 'dod-series', err });
      },
    },
  );

  const data = useMemo(() => {
    if (!dodEnabled || isEmpty(response)) {
      return defaultResponseValue;
    }
    return response;
  }, [dodEnabled, response]);

  return { data, isFetching, isError };
}
